import React from 'react';
import {
  Clapperboard, FileText, HardDrive, Image as ImageIcon, LayoutGrid, MapPin, Package, Paperclip, PenTool, Share2, Sparkles, Type,
  User as UserIcon, Video as VideoIcon, Wand2, type LucideIcon,
} from 'lucide-react';
import { NodeType } from '../../types';

/**
 * Every kind of node has a colour of its own, so a node is recognised at a glance: on the
 * canvas (its header and icon), in the rail that adds nodes, and on the minimap. The colours
 * are one family, the brand's five (lavender, flame, mint, lemon, blush) and the tints that
 * lie between them, all at the same lightness. This file is the one place that says which
 * node has which; the colours themselves are the `[data-tone]` rules in index.css.
 */
export type NodeTone =
  | 'lavender' | 'flame' | 'mint' | 'lemon' | 'blush'
  | 'sky' | 'sage' | 'peach' | 'coral' | 'orchid' | 'teal'
  | 'ink';

interface NodeKind {
  tone: NodeTone;
  /** What the kind is called where a node of it has no title of its own. */
  label: string;
  icon: LucideIcon;
}

const KINDS: Record<NodeType, NodeKind> = {
  [NodeType.IMAGE_GENERATOR]: { tone: 'lavender', label: 'Image', icon: Sparkles },
  [NodeType.VIDEO_GENERATOR]: { tone: 'flame', label: 'Video', icon: VideoIcon },
  [NodeType.MEDIA_REFERENCE]: { tone: 'mint', label: 'Reference', icon: Paperclip },
  [NodeType.AUDIO_REFERENCE]: { tone: 'mint', label: 'Reference', icon: Paperclip },
  [NodeType.IMAGE_VIEWER]: { tone: 'mint', label: 'Reference', icon: ImageIcon },
  [NodeType.SKETCH_PAD]: { tone: 'blush', label: 'Sketch', icon: PenTool },
  [NodeType.SCRIPT_PROCESSOR]: { tone: 'lemon', label: 'Script', icon: FileText },
  [NodeType.WRITE_NODE]: { tone: 'sky', label: 'Write', icon: Wand2 },
  [NodeType.TEXT_PROMPT]: { tone: 'sky', label: 'Text', icon: Type },
  [NodeType.STORYBOARD_NODE]: { tone: 'orchid', label: 'Storyboard', icon: LayoutGrid },
  [NodeType.CHARACTER_NODE]: { tone: 'coral', label: 'Character', icon: UserIcon },
  [NodeType.PROP_NODE]: { tone: 'teal', label: 'Prop', icon: Package },
  [NodeType.LOCATION_NODE]: { tone: 'sage', label: 'Location', icon: MapPin },
  [NodeType.SHARE_NODE]: { tone: 'ink', label: 'Post', icon: Share2 },
  [NodeType.STORAGE_BROWSER]: { tone: 'ink', label: 'Storage', icon: HardDrive },
  [NodeType.QUICK_EDITOR]: { tone: 'ink', label: 'Edit', icon: Clapperboard },
};

const FALLBACK: NodeKind = { tone: 'ink', label: 'Node', icon: Sparkles };

export const nodeKind = (type: NodeType): NodeKind => KINDS[type] ?? FALLBACK;
export const nodeTone = (type: NodeType): NodeTone => nodeKind(type).tone;

/** The kind's icon, for a node's header and for the rail that adds nodes. It takes the size its holder gives it. */
export const NodeKindIcon: React.FC<{ type: NodeType; size?: number; className?: string }> = ({ type, size = 14, className }) => {
  const Icon = nodeKind(type).icon;
  return <Icon size={size} className={className} aria-hidden="true" />;
};

/**
 * The tones as colours, for what is drawn and not styled (the minimap's SVG). They are the
 * same values as `--tone` and `--tone-deep` in index.css; change both together.
 */
export const TONE_COLORS: Record<NodeTone, { solid: string; deep: string }> = {
  lavender: { solid: '#AB9FF2', deep: '#4A3AFF' },
  flame: { solid: '#FF8A4C', deep: '#A63F07' },
  mint: { solid: '#7FD9B5', deep: '#0B5C46' },
  lemon: { solid: '#F2D23C', deep: '#6B5A00' },
  blush: { solid: '#F58BA6', deep: '#8A2543' },
  sky: { solid: '#8EC9F5', deep: '#145A8C' },
  sage: { solid: '#B9D97A', deep: '#46601A' },
  peach: { solid: '#FFB37A', deep: '#8A4A12' },
  coral: { solid: '#FF8F85', deep: '#9C2F26' },
  orchid: { solid: '#D69AEB', deep: '#6F2A8A' },
  teal: { solid: '#5CCFC9', deep: '#0C5E5A' },
  ink: { solid: '#9B9BAB', deep: '#11121A' },
};
